import { useCallback, useMemo, useState } from 'react';

import type { IIndirectTaxRegistrationV2WithSettings } from '@commenda/api/indirect-tax/registrations/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@commenda/kakashi';
import { toTitleCasePreservingAcronyms } from '@commenda/utils';

import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { IndirectTaxRegistrationCredentialsStep } from '../IndirectTaxRegistrationCredentialsStep';
import { IndirectTaxConditionsStep } from '../../wizards/existing/steps';
import { IndirectTaxRegistrationSelector } from '../IndirectTaxRegistrationSelector';
import {
  useIndirectTaxEditRegistrationWizard,
  EditWizardStep,
} from '../../wizards/edit';

interface IndirectTaxEditRegistrationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  registrationData: IIndirectTaxRegistrationV2WithSettings;
  setNotification: (notification: {
    title: string;
    message: string;
    show: boolean;
    valid: boolean;
  }) => void;
  onSuccess: () => void;
  isLocalRegistration?: boolean;
}

const STEP_HEADINGS: Record<EditWizardStep, { title: string; description: string }> = {
  [EditWizardStep.REGISTRATION_INFO]: {
    title: 'Edit registration',
    description: 'Update your registration details and filing settings.',
  },
  [EditWizardStep.CONDITIONS]: {
    title: 'Registration requirements',
    description: 'Registration conditions set when this registration was created.',
  },
  [EditWizardStep.CREDENTIALS]: {
    title: 'Update credentials',
    description: 'Update your portal credentials to enable automatic filing.',
  },
};

export function IndirectTaxEditRegistrationWizard({
  isOpen,
  onClose,
  corporationId,
  registrationData,
  setNotification,
  onSuccess,
  isLocalRegistration = false,
}: IndirectTaxEditRegistrationWizardProps) {
  const wizard = useIndirectTaxEditRegistrationWizard({
    isOpen,
    corporationId,
    registrationData,
  });
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const hasUnsavedChanges = useMemo(() => {
    const { form } = wizard;
    const initialTaxTypes = registrationData.taxTypes ?? [];
    const initialFrequency = registrationData.frequencies?.[0] ?? '';
    const initialEffectiveStartDate = registrationData.effectiveStartDate ?? '';
    const taxTypesChanged =
      form.taxTypes.length !== initialTaxTypes.length ||
      form.taxTypes.some((t) => !initialTaxTypes.includes(t));
    return Boolean(
      taxTypesChanged ||
      form.frequency !== initialFrequency ||
      form.effectiveStartDate !== initialEffectiveStartDate ||
      Object.values(form.registrationFields).some((v) => v) ||
      Object.values(form.credentialFields).some((v) => v)
    );
  }, [wizard, registrationData]);

  const performClose = useCallback(() => {
    setIsDiscardConfirmOpen(false);
    onClose();
    setTimeout(() => wizard.resetForm(), 100);
  }, [onClose, wizard]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    performClose();
  }, [hasUnsavedChanges, performClose]);

  const handleSubmit = useCallback(async () => {
    const result = await wizard.submit();

    if (!result.success) {
      setNotification({
        message: result.error || 'Failed to update registration.',
        title: 'Error',
        show: true,
        valid: false,
      });
      return;
    }

    setNotification({
      message: 'Registration updated successfully.',
      title: 'Success',
      show: true,
      valid: true,
    });

    performClose();
    onSuccess();
  }, [wizard, setNotification, performClose, onSuccess]);

  const handleNext = useCallback(async () => {
    if (wizard.currentStep === EditWizardStep.CREDENTIALS) {
      await handleSubmit();
    } else if (
      wizard.currentStep === EditWizardStep.CONDITIONS &&
      wizard.shouldSkipCredentials
    ) {
      await handleSubmit();
    } else {
      wizard.goToNextStep();
    }
  }, [wizard, handleSubmit]);

  const canProceed = (() => {
    switch (wizard.currentStep) {
      case EditWizardStep.REGISTRATION_INFO:
        return wizard.canProceedStep1;
      case EditWizardStep.CONDITIONS:
        return wizard.canProceedStep2;
      case EditWizardStep.CREDENTIALS:
        return wizard.canProceedStep3;
      default:
        return false;
    }
  })();

  const isLastStep =
    wizard.currentStep === EditWizardStep.CREDENTIALS ||
    (wizard.currentStep === EditWizardStep.CONDITIONS && wizard.shouldSkipCredentials);

  const getDialogTitle = () => {
    if (wizard.currentStep === EditWizardStep.CREDENTIALS && wizard.selectedPortal)
      return toTitleCasePreservingAcronyms(wizard.selectedPortal.portalName)
    return STEP_HEADINGS[wizard.currentStep].title
  }

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="min-w-[600px] flex flex-col max-h-[90vh]"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <div className="flex flex-col gap-xs">
            <p className="txt-large-bold text-text-primary">
              {getDialogTitle()}
            </p>
            <p className="txt-small-regular text-text-tertiary">
              {STEP_HEADINGS[wizard.currentStep].description}
            </p>
          </div>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-3xl">
              {wizard.currentStep === EditWizardStep.REGISTRATION_INFO && (
                <IndirectTaxRegistrationSelector
                  availableRegistrations={wizard.availableRegistrations}
                  isLoading={wizard.isLoadingRegistrations}
                  selectedRegistrationId={wizard.selectedRegistration?.registrationId ?? ''}
                  onRegistrationChange={() => { }}
                  selectedTaxTypeOption={wizard.selectedTaxTypeOption}
                  selectedRegistrationTaxTypeId={registrationData.registrationTaxTypeId}
                  selectedTaxTypes={wizard.form.taxTypes}
                  onTaxTypeOptionChange={() => { }}
                  onOptionalTaxTypeToggle={(taxType, checked) => {
                    const current = wizard.form.taxTypes;
                    const updated = checked
                      ? [...current, taxType]
                      : current.filter((t) => t !== taxType);
                    wizard.setOptionalTaxTypes(updated);
                  }}
                  selectedFrequency={wizard.form.frequency}
                  onFrequencyChange={wizard.setFrequency}
                  effectiveStartDate={wizard.form.effectiveStartDate}
                  onEffectiveDateChange={wizard.setEffectiveStartDate}
                  taxTypeDetailsMap={wizard.taxTypeDetailsMap}
                  isEditMode
                  registrationName={
                    registrationData.registrationName
                      ? toTitleCasePreservingAcronyms(registrationData.registrationName)
                      : registrationData.jurisdictionName
                        ? toTitleCasePreservingAcronyms(registrationData.jurisdictionName)
                        : undefined
                  }
                />
              )}

              {wizard.currentStep === EditWizardStep.CONDITIONS && (
                <IndirectTaxConditionsStep
                  prerequisiteCondition={wizard.prerequisiteCondition}
                  memberCountryCondition={wizard.memberCountryCondition}
                  availablePrerequisites={[]}
                  selectedPrerequisiteRegistrationTaxTypeId={wizard.resolvedPrerequisiteTaxTypeId}
                  onPrerequisiteChange={() => { }}
                  selectedMemberCountryRegistrationTaxTypeId={
                    registrationData.registeredThroughRegistrationTaxTypeId ?? ''
                  }
                  onMemberCountryChange={() => { }}
                  isEditMode
                />
              )}

              {wizard.currentStep === EditWizardStep.CREDENTIALS && (
                <IndirectTaxRegistrationCredentialsStep
                  selectedPortal={wizard.selectedPortal}
                  corporationId={corporationId}
                  isCheckingCredentials={wizard.isCheckingCredentials}
                  credentialsFound={wizard.credentialsFound}
                  forceShowFields
                  registrationFields={wizard.form.registrationFields}
                  credentialFields={wizard.form.credentialFields}
                  onRegistrationFieldChange={wizard.setRegistrationField}
                  onCredentialFieldChange={wizard.setCredentialField}
                />
              )}
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {wizard.currentStep !== EditWizardStep.REGISTRATION_INFO && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={wizard.goToPreviousStep}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-sm">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleClose}
              >
                Close
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canProceed || wizard.isSubmitting}
                isLoading={wizard.isSubmitting}
                onClick={handleNext}
              >
                {isLastStep ? 'Save changes' : 'Continue'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
      <ConfirmationModal
        isOpen={isDiscardConfirmOpen}
        title="Discard changes?"
        message="Your unsaved changes to this registration will be lost."
        onClose={() => setIsDiscardConfirmOpen(false)}
        onConfirm={performClose}
        cancelButtonText="Keep editing"
        confirmButtonText="Discard"
        tone="destructive"
      />
    </Dialog>
  );
}
